import axios from "axios"
import { Clock, ExternalLink, FileQuestion } from "lucide-react"
import { useEffect } from "react"
import { Navigate, useParams } from "react-router-dom"
import { PostTaxonomyLinks, usePost } from "@/entities/post"
import { useRecordView } from "@/entities/view-history"
import { FavoriteButton } from "@/features/toggle-favorite"
import { useHaptic } from "@/shared/hooks/use-haptic"
import { openExternalLink } from "@/shared/lib/open-external-link"
import { Button } from "@/shared/ui/button"
import { EmptyState } from "@/shared/ui/empty-state"
import { HapticLink } from "@/shared/ui/haptic-link"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { PageTransition } from "@/shared/ui/page-transition"
import { Skeleton } from "@/shared/ui/skeleton"
import { PostMediaViewer } from "@/widgets/post-media-viewer"
import { PostDescription } from "./post-description"

const CONTAINER = "container mx-auto max-w-3xl px-3 pb-6 sm:px-4 lg:pb-8"

function PostShell({ children }: { children: React.ReactNode }) {
	return (
		<div className="relative isolate -mt-[var(--app-body-padding-top)] pt-[var(--app-body-padding-top)]">
			<div
				aria-hidden
				className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-64 bg-[radial-gradient(120%_70%_at_50%_0%,color-mix(in_oklab,var(--color-primary)_24%,transparent),transparent_70%)]"
			/>
			<div className={CONTAINER}>{children}</div>
		</div>
	)
}

function PostDetailSkeleton() {
	return (
		<PageTransition>
			<PostShell>
				<Skeleton className="aspect-video w-full rounded-2xl" />
				<div className="pt-5">
					<Skeleton className="h-3 w-28" />
					<Skeleton className="mt-3 h-7 w-full" />
					<Skeleton className="mt-2 h-7 w-2/3" />
					<Skeleton className="mt-4 h-4 w-5/6" />
					<div className="mt-6 flex gap-2">
						<Skeleton className="h-11 flex-1 rounded-full" />
						<Skeleton className="size-11 rounded-full" />
					</div>
					<Skeleton className="mt-6 h-48 w-full rounded-3xl" />
					<div className="mt-6 flex gap-2">
						<Skeleton className="h-7 w-24 rounded-full" />
						<Skeleton className="h-7 w-32 rounded-full" />
					</div>
				</div>
			</PostShell>
		</PageTransition>
	)
}

export function PostDetailPage() {
	const { id } = useParams<{ id: string }>()
	const { data: post, isLoading, error, refetch } = usePost(id!)
	const recordView = useRecordView()
	const { triggerHaptic } = useHaptic()
	const postId = post?.id

	useEffect(() => {
		if (postId) {
			recordView.mutate({ postId })
		}
	}, [postId, recordView.mutate])

	if (!id) {
		return <Navigate to="/" replace />
	}

	if (isLoading) {
		return <PostDetailSkeleton />
	}

	if (error && !(axios.isAxiosError(error) && error.response?.status === 404))
		return (
			<div className={CONTAINER}>
				<LoadErrorState onRetry={() => void refetch()} />
			</div>
		)

	if (error || !post) {
		return (
			<PageTransition>
				<PostShell>
					<EmptyState
						icon={FileQuestion}
						title="Пост не найден"
						action={
							<Button asChild>
								<HapticLink to="/">На главную</HapticLink>
							</Button>
						}
					>
						Материал удалили или ссылка устарела.
					</EmptyState>
				</PostShell>
			</PageTransition>
		)
	}

	const format = post.formats[0]?.name
	const duration = post.durations[0]?.name
	const media = post.media ?? []

	const openOriginal = () => {
		if (!post.postUrl) return
		triggerHaptic("medium")
		openExternalLink(post.postUrl)
	}

	return (
		<PageTransition>
			<PostShell>
				{media.length > 0 ? <PostMediaViewer media={media} postId={post.id} /> : null}

				<div className="pt-5">
					{(format || duration) && (
						<div className="flex items-center justify-between gap-3">
							<p className="truncate text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
								{format}
							</p>
							{duration && (
								<span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
									<Clock className="size-3.5" />
									{duration}
								</span>
							)}
						</div>
					)}

					<h1 className="mt-1.5 text-2xl leading-tight font-bold tracking-tight sm:text-3xl">
						{post.title}
					</h1>
					{post.shortDescription ? (
						<p className="mt-2 text-sm leading-snug text-muted-foreground sm:text-base">
							{post.shortDescription}
						</p>
					) : null}

					<div className="mt-6 flex items-center gap-2">
						{post.postUrl ? (
							<>
								<button
									type="button"
									onClick={openOriginal}
									className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/25 transition-transform duration-150 ease-[var(--ease-out-soft)] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
								>
									Открыть в Telegram
									<ExternalLink className="size-4" />
								</button>
								<span className="flex shrink-0 items-center justify-center rounded-full bg-white/[0.06] ring-1 ring-white/10">
									<FavoriteButton postId={post.id} variant="icon" />
								</span>
							</>
						) : (
							<FavoriteButton postId={post.id} />
						)}
					</div>

					{post.description ? (
						<div className="mt-6">
							<PostDescription text={post.description} />
						</div>
					) : null}

					<PostTaxonomyLinks post={post} className="mt-6" />
				</div>
			</PostShell>
		</PageTransition>
	)
}
